import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import type { OrganizerPhoto } from "@ui/components/PhotoOrganizer";
import { SoftButton } from "@ui/components/SoftButton";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { PhotosField, photosFieldCopy } from "@/components/ListingFields/PhotosField";
import type { UploadFileItem } from "@/components/PhotoUploadModal/PhotoUploadModal";

const COLOR = "green" as const;
const LISTING_TYPE = "service" as const;

export interface ServicePhotosStepViewProps {
  /** Wizard progress. */
  currentStage: number;
  totalStages: number;
  onBack?: () => void;
  /** Wizard top-bar tertiary actions (Questions / Finish later). */
  topBarActions: ReactNode;
  /** Primary "Continue" button. */
  onPrimary: () => void;
  primaryPending: boolean;
  /** Gallery state. */
  photos: OrganizerPhoto[];
  minPhotos: number;
  photosError?: string;
  onAddMore: () => void;
  onDeletePhoto: (photoId: string) => void;
  onReorder: (orderedIds: string[]) => void;
  /** Upload modal. */
  uploadModalOpen: boolean;
  uploadFiles: UploadFileItem[];
  uploadIsUploading: boolean;
  uploadAllCompleted: boolean;
  onUploadAddFiles: (files: FileList) => void;
  onUploadConfirm: () => void;
  onUploadModalOpenChange: (open: boolean) => void;
  onUploadRemoveFile: (id: string) => void;
  /** Delete confirmation dialog. */
  deleteModalOpen: boolean;
  onDeleteConfirm: () => void;
  onDeleteModalOpenChange: (open: boolean) => void;
}

export function ServicePhotosStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  photos,
  minPhotos,
  photosError,
  onAddMore,
  onDeletePhoto,
  onReorder,
  uploadModalOpen,
  uploadFiles,
  uploadIsUploading,
  uploadAllCompleted,
  onUploadAddFiles,
  onUploadConfirm,
  onUploadModalOpenChange,
  onUploadRemoveFile,
  deleteModalOpen,
  onDeleteConfirm,
  onDeleteModalOpenChange,
}: ServicePhotosStepViewProps) {
  const hasPhotos = photos.length > 0;
  const { heading, helper } = photosFieldCopy({ hasPhotos, listingType: LISTING_TYPE, minPhotos });

  const field = (
    <PhotosField
      allUploadsCompleted={uploadAllCompleted}
      error={photosError}
      isDeleteModalOpen={deleteModalOpen}
      isUploadModalOpen={uploadModalOpen}
      isUploading={uploadIsUploading}
      listingType={LISTING_TYPE}
      minPhotos={minPhotos}
      onAddFiles={onUploadAddFiles}
      onAddPhotos={onAddMore}
      onConfirmDelete={onDeleteConfirm}
      onConfirmUpload={onUploadConfirm}
      onDeleteModalOpenChange={onDeleteModalOpenChange}
      onDeletePhoto={onDeletePhoto}
      onRemoveUploadFile={onUploadRemoveFile}
      onReorderPhotos={onReorder}
      onUploadModalOpenChange={onUploadModalOpenChange}
      photos={photos}
      uploadFiles={uploadFiles}
    />
  );

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      {hasPhotos ? (
        <Wizard.EntitiesStage
          heading={heading}
          helper={helper}
          headerAction={
            <SoftButton aria-label={t("web.listings.fields.photos.add_more")} onPress={onAddMore} size="md">
              <Icon name="add" size="20" />
            </SoftButton>
          }
        >
          {field}
        </Wizard.EntitiesStage>
      ) : (
        <Wizard.Stage heading={heading} helper={helper}>
          {field}
        </Wizard.Stage>
      )}
    </Wizard>
  );
}
